import React from 'react';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { decimalToHHMM, HHMMToDecimal } from './multiplierUtils';

interface ThresholdSectionProps {
  type: 'shift' | 'weekly';
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  threshold: number | null;
  onThresholdChange: (value: number | null) => void;
  billingStructures: string[] | null;
  onBillingStructuresChange: (value: string[] | null) => void;
  showAppliesTo: boolean;
  error?: string;
  /** When true and type='weekly' and enabled=false, shows a warning that Overtime Settings will hide */
  isLastWeeklyThreshold?: boolean;
  /** Sheet mode — warning only shows in edit mode */
  mode?: 'create' | 'edit';
  /** Called on blur with the current committed threshold value */
  onCommit?: (value: number | null) => void;
}

const CONFIG = {
  shift: {
    label: 'Per Shift',
    icon: '⏱',
    suffix: 'hrs into shift',
    activeColor: 'text-primary',
    activeBg: 'bg-primary',
    badgeBg: 'bg-primary/10',
    badgeBorder: 'border-primary/20',
    badgeText: 'text-primary',
  },
  weekly: {
    label: 'Weekly',
    icon: '📅',
    suffix: 'hrs per week',
    activeColor: 'text-purple-600 dark:text-purple-400',
    activeBg: 'bg-purple-600',
    badgeBg: 'bg-purple-500/10',
    badgeBorder: 'border-purple-500/20',
    badgeText: 'text-purple-600 dark:text-purple-400',
  },
} as const;

export const ThresholdSection: React.FC<ThresholdSectionProps> = ({
  type,
  enabled,
  onToggle,
  threshold,
  onThresholdChange,
  billingStructures,
  onBillingStructuresChange,
  showAppliesTo,
  error,
  isLastWeeklyThreshold,
  mode = 'edit',
  onCommit,
}) => {
  const config = CONFIG[type];
  const displayValue = threshold !== null ? decimalToHHMM(threshold) : '';

  const [inputValue, setInputValue] = React.useState(displayValue);
  const isFocusedRef = React.useRef(false);

  // Sync from parent only when NOT focused (e.g., toggle pre-fills a suggested value)
  React.useEffect(() => {
    if (!isFocusedRef.current) {
      setInputValue(displayValue);
    }
  }, [displayValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);

    // Commit parsed value so validation stays in sync
    if (raw.trim() === '') {
      onThresholdChange(null);
    } else {
      const decimal = HHMMToDecimal(raw.trim());
      if (decimal !== null) {
        onThresholdChange(decimal);
      }
    }
  };

  const handleInputFocus = () => {
    isFocusedRef.current = true;
  };

  const handleInputBlur = () => {
    isFocusedRef.current = false;
    // Normalize display — HHMMToDecimal already handles bare numbers (e.g. "40" → 40.0),
    // so decimalToHHMM will normalize to "40:00" from the committed threshold
    if (threshold !== null) {
      setInputValue(decimalToHHMM(threshold));
    }
    onCommit?.(threshold);
  };

  const toggleBillingStructure = (structure: string) => {
    const current = billingStructures || [];
    const next = current.includes(structure)
      ? current.filter(s => s !== structure)
      : [...current, structure];
    onBillingStructuresChange(next.length > 0 ? next : null);
  };

  return (
    <div
      className={cn(
        'border rounded transition-opacity flex flex-col',
        'bg-surface-1 shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]',
        enabled
          ? 'border-border dark:border-[oklch(0.55_0.08_75)] dark:shadow-[0_0_0_1px_oklch(0.55_0.08_75_/_0.15),inset_0_1px_2px_rgba(0,0,0,0.15)]'
          : 'border-border dark:border-[oklch(0.25_0_0)] opacity-50'
      )}
    >
      {/* Row 1: Label + Toggle — constant padding to avoid layout shift on toggle */}
      <div className="px-3.5 pt-2.5 pb-2.5">
        <div className="flex justify-between items-center">
          <span
            className={cn(
              'text-xs font-semibold uppercase tracking-wide',
              enabled ? config.activeColor : 'text-muted-foreground'
            )}
          >
            {config.icon} {config.label}
          </span>
          <Switch
            checked={enabled}
            onCheckedChange={onToggle}
            className="h-[18px] w-[34px] [&>span]:h-3.5 [&>span]:w-3.5"
          />
        </div>
      </div>

      {/* Warning when disabling last weekly threshold */}
      <AnimatedCollapse open={type === 'weekly' && !enabled && !!isLastWeeklyThreshold && mode === 'edit'}>
        <div className="px-3.5 -mt-1 pb-2">
          <p className="text-[12px] sm:text-[13px] text-muted-foreground">
            Disabling this will hide Overtime Settings<span className="hidden sm:inline"> (Work Week, Weekly Hours Count, Overlapping Overtime)</span>.
          </p>
        </div>
      </AnimatedCollapse>

      {/* Explain what shift toggle does when disabled */}
      <AnimatedCollapse open={type === 'shift' && !enabled}>
        <div className="px-3.5 -mt-1 pb-2">
          <p className="text-[12px] sm:text-[13px] text-muted-foreground">
            Enable to trigger this rate after a set number of hours into a shift.
          </p>
        </div>
      </AnimatedCollapse>

      {/* Explain what weekly toggle does when disabled */}
      <AnimatedCollapse open={type === 'weekly' && !enabled && mode === 'create'}>
        <div className="px-3.5 -mt-1 pb-2">
          <p className="text-[12px] sm:text-[13px] text-muted-foreground">
            Enable to set a weekly hour threshold. This adds additional Overtime Settings<span className="hidden sm:inline"> (work week, overlapping overtime, hour counting rules)</span> below.
          </p>
        </div>
      </AnimatedCollapse>

      {/* Row 2: Threshold input + Applies To (when enabled) */}
      <AnimatedCollapse open={enabled} className="flex-1">
        <div className="px-3.5 pb-2.5">
          {/* Responsive: centered column on mobile, inline row on desktop */}
          <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-baseline sm:gap-1.5">
            <span className="hidden sm:inline text-xs text-muted-foreground">after</span>
            <input
              id={`threshold-${type}`}
              type="text"
              value={inputValue}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onChange={handleInputChange}
              placeholder="HH:MM"
              className={cn(
                'w-24 sm:w-[76px] py-2 sm:py-1.5 px-3 sm:px-2 bg-surface-2 border border-border rounded',
                'text-lg sm:text-sm font-mono text-center text-foreground outline-none',
                'shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]',
                'focus:ring-2 focus:ring-primary/30 focus:border-primary/50',
                error && 'border-destructive focus:ring-destructive/30'
              )}
            />
            <span className="text-[10px] sm:text-xs text-muted-foreground">{config.suffix}</span>
          </div>
          {error && (
            <p className="text-xs text-destructive mt-1 text-center">{error}</p>
          )}
        </div>

        {/* Sub-row: Applies To (when enabled + mixed billing) */}
        {showAppliesTo && (
          <div className="pl-3.5 pr-2.5 py-1.5 border-t border-border bg-surface-0 flex items-center justify-between">
            <span className="text-[12px] sm:text-[13px] text-muted-foreground uppercase tracking-wide">
              Applies to
            </span>
            <div className="flex gap-1.5">
              {['day_rate', 'hourly'].map((structure) => {
                const isActive = billingStructures?.includes(structure);
                return (
                  <button
                    key={structure}
                    type="button"
                    onClick={() => toggleBillingStructure(structure)}
                    className={cn(
                      'text-[10px] sm:text-[12px] px-2 py-0.5 rounded cursor-pointer border transition-colors',
                      isActive
                        ? `${config.badgeBg} ${config.badgeText} ${config.badgeBorder}`
                        : 'bg-surface-2 text-muted-foreground border-border'
                    )}
                  >
                    {structure === 'day_rate' ? 'Day Rate' : 'Hourly'}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </AnimatedCollapse>
    </div>
  );
};
